import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import { useRefresh } from "../contexts/RefreshContext";
import { fmtCur, fmtCurCompact, fmtNum, fmtRate, fmtDate } from "../lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { Receipt, TrendUp, Target, CaretRight, X, MagnifyingGlass, Warning } from "@phosphor-icons/react";

const monthLabel = (m) => {
  const [y, mo] = (m || "").split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return mo ? `${names[+mo - 1]} '${y.slice(2)}` : m;
};

function Stat({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div data-testid={testid || `sales-stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
      style={{ background: "#fff", border: "1px solid var(--omh-border)", borderTop: `3px solid ${accent}`,
        borderRadius: 12, padding: "18px 20px", flex: 1, minWidth: 190 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, color: "var(--omh-text-muted)" }}>{label}</span>
        {Icon && <Icon size={20} weight="duotone" color={accent} />}
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, marginTop: 10, fontFamily: "Inter, sans-serif", color: "var(--omh-text)" }}>{value}</div>
      {sub && <div style={{ fontSize: 12.5, color: "var(--omh-text-muted)", marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

const ccyLine = (obj) => Object.entries(obj || {}).filter(([, v]) => v)
  .map(([c, v]) => fmtCurCompact(v, c)).join(" · ") || "—";

// Custom chart tooltip: explains the plan (Expected + fixed Spillover backlog) and the actual
// split (Current-month sale vs Spillover sale), plus how much value shifted month due to later
// CFS cut-off dates.
function SalesChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload || {};
  const Row = ({ c, k, v }) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 18, fontSize: 12 }}>
      <span style={{ color: "#475569", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: "inline-block" }} />{k}</span>
      <b style={{ color: "#0f172a", fontFamily: "Inter, sans-serif" }}>{fmtCur(v, "INR")}</b>
    </div>
  );
  return (
    <div data-testid="sales-chart-tooltip" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", boxShadow: "0 8px 24px rgba(15,23,42,0.12)", minWidth: 240 }}>
      <div style={{ fontWeight: 800, fontSize: 12.5, marginBottom: 6, color: "#0f172a" }}>{label}</div>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, margin: "2px 0" }}>Plan (expected)</div>
      <Row c="#C4B5FD" k="Expected" v={d.Expected || 0} />
      {d.Spillover > 0 && <Row c="#F59E0B" k="Spillover (backlog, fixed)" v={d.Spillover} />}
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, margin: "8px 0 2px" }}>Actual (shipped)</div>
      <Row c="#0369A1" k="Current-month sale" v={d.CurrentSale || 0} />
      {d.SpilloverSale > 0 && <Row c="#0E7490" k="Spillover sale (backlog cleared)" v={d.SpilloverSale} />}
      {(d.cfsOut > 0 || d.cfsIn > 0) && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px dashed #e2e8f0", fontSize: 11, color: "#b45309" }}>
          {d.cfsOut > 0 && <div>↦ {fmtCur(d.cfsOut, "INR")} shifted to a later month (CFS cut-off later than plan)</div>}
          {d.cfsIn > 0 && <div>↤ {fmtCur(d.cfsIn, "INR")} shifted in from an earlier planned month</div>}
        </div>
      )}
    </div>
  );
}

export default function SalesMonthly() {
  const [sp] = useSearchParams();
  const { tick } = useRefresh();
  const plant = sp.get("plant") || "";
  const mode = sp.get("mode") || "";
  const [data, setData] = useState(null);

  // Drill-down: month → customer-level sales → part-level sales
  const [drillMonth, setDrillMonth] = useState(null);   // "YYYY-MM"
  const [drillCustomer, setDrillCustomer] = useState(null); // customer_name
  const [custRows, setCustRows] = useState(null);
  const [partRows, setPartRows] = useState(null);
  const [drillPart, setDrillPart] = useState(null);       // part_no
  const [partDetail, setPartDetail] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [partsView, setPartsView] = useState("fulfilment"); // "revenue" | "fulfilment"
  const [priceBasis, setPriceBasis] = useState("invoice");   // "invoice" (default) | "po"
  const [spill, setSpill] = useState(false);                 // Spillover roll-forward — opt-in (off = original plan)
  const [showBacklog, setShowBacklog] = useState(false);     // Overdue-backlog study table
  const [backlog, setBacklog] = useState(null);
  // Spillover-sale / CFS-shift drill modal
  const [listDrill, setListDrill] = useState(null);   // {kind:'spillover'|'cfs', month}
  const [listData, setListData] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const openListDrill = (kind, month) => {
    setListDrill({ kind, month });
    setListData(null);
    setListLoading(true);
    const ep = kind === "spillover" ? "spillover-cleared" : "cfs-shifted";
    const q = new URLSearchParams({ month });
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    if (kind === "cfs") q.set("price_basis", priceBasis);
    api.get(`/ms/sales-monthly/${ep}?${q.toString()}`)
      .then((r) => setListData(r.data))
      .catch(() => setListData({ rows: [], count: 0 }))
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    if (!spill || !showBacklog) { return; }
    setBacklog(null);
    const q = new URLSearchParams({ price_basis: priceBasis });
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    api.get(`/ms/sales-monthly/overdue-backlog?${q.toString()}`)
      .then((r) => setBacklog(r.data)).catch(() => setBacklog({ rows: [], count: 0 }));
  }, [spill, showBacklog, plant, mode, priceBasis]);
  useEffect(() => { if (!spill) setShowBacklog(false); }, [spill]);

  const loadParts = (name, view) => {
    setPartRows(null);
    setDrillPart(null);
    setPartDetail(null);
    setDrillLoading(true);
    const q = new URLSearchParams({ month: drillMonth, customer: name, view, price_basis: priceBasis });
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    if (spill) q.set("spillover", "true");
    api.get(`/ms/sales-monthly/parts?${q.toString()}`)
      .then((r) => setPartRows(r.data?.parts || []))
      .catch(() => setPartRows([]))
      .finally(() => setDrillLoading(false));
  };
  const togglePartsView = (v) => { setPartsView(v); if (drillCustomer) loadParts(drillCustomer, v); };

  const openMonth = (m) => {
    if (!m) return;
    setDrillMonth(m);
    setDrillCustomer(null);
    setPartRows(null);
    setCustRows(null);
    setDrillLoading(true);
    const q = new URLSearchParams({ month: m });
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    q.set("price_basis", priceBasis);
    if (spill) q.set("spillover", "true");
    api.get(`/ms/sales-monthly/customers?${q.toString()}`)
      .then((r) => setCustRows(r.data?.customers || []))
      .catch(() => setCustRows([]))
      .finally(() => setDrillLoading(false));
  };

  const openCustomer = (name) => {
    setDrillCustomer(name);
    loadParts(name, partsView);
  };

  const openPart = (part) => {
    setDrillPart(part);
    setPartDetail(null);
    setDrillLoading(true);
    const q = new URLSearchParams({ month: drillMonth, customer: drillCustomer, part, view: partsView, price_basis: priceBasis });
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    if (spill) q.set("spillover", "true");
    api.get(`/ms/sales-monthly/part-detail?${q.toString()}`)
      .then((r) => setPartDetail(r.data || { orders: [], invoices: [] }))
      .catch(() => setPartDetail({ orders: [], invoices: [] }))
      .finally(() => setDrillLoading(false));
  };

  const closeDrill = () => { setDrillMonth(null); setDrillCustomer(null); setCustRows(null); setPartRows(null); setDrillPart(null); setPartDetail(null); };

  useEffect(() => {
    const q = new URLSearchParams();
    if (plant) q.set("plant", plant);
    if (mode) q.set("mode", mode);
    q.set("price_basis", priceBasis);
    if (spill) q.set("spillover", "true");
    api.get(`/ms/sales-monthly?${q.toString()}`).then((r) => setData(r.data)).catch(() => setData({ series: [] }));
  }, [plant, mode, priceBasis, spill, tick]);

  // Re-fetch whatever drill level is currently open when the price basis / spillover toggle flips.
  useEffect(() => {
    if (drillMonth) {
      const q = new URLSearchParams({ month: drillMonth, price_basis: priceBasis });
      if (plant) q.set("plant", plant);
      if (mode) q.set("mode", mode);
      if (spill) q.set("spillover", "true");
      api.get(`/ms/sales-monthly/customers?${q.toString()}`)
        .then((r) => setCustRows(r.data?.customers || [])).catch(() => {});
    }
    if (drillCustomer) loadParts(drillCustomer, partsView);
    if (drillPart) openPart(drillPart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBasis, spill]);

  const series = data?.series || [];
  const tm = data?.this_month;
  const curMonth = tm?.month;
  const achievement = tm && tm.expected_inr > 0 ? Math.round((tm.actual_inr / tm.expected_inr) * 100) : null;

  const chart = useMemo(() => (data?.series || []).map((s) => ({
    month: monthLabel(s.month), raw: s.month,
    Expected: Math.round(s.expected_inr), Actual: Math.round(s.actual_inr),
    Spillover: Math.round(s.spillover_inr || 0),
    SpilloverSale: Math.round(s.spillover_sale_inr || 0),
    CurrentSale: Math.round(s.current_sale_inr || 0),
    cfsOut: Math.round(s.cfs_shift_out_inr || 0),
    cfsIn: Math.round(s.cfs_shift_in_inr || 0),
    eb: s.expected_by_ccy, ab: s.actual_by_ccy,
  })), [data]);

  // shared table styles for the overdue-backlog panel (the drill modal defines its own copies)
  const bcell = { padding: "7px 9px", fontSize: 12.5, whiteSpace: "normal", wordBreak: "break-word" };
  const brt = { ...bcell, textAlign: "right", fontFamily: "Inter, sans-serif" };
  const bft = { ...brt, fontWeight: 800, borderTop: "2px solid var(--omh-border)", background: "#f8fafc" };
  const bftl = { ...bcell, fontWeight: 800, borderTop: "2px solid var(--omh-border)", background: "#f8fafc" };

  return (
    <Layout breadcrumb={["Home", "Monthly Sales"]}>
      <div className="jde-page">
        <div style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }} data-testid="sales-stats">
          <Stat testid="sales-stat-this-month-actual" label={`This Month · Actual${curMonth ? ` · ${monthLabel(curMonth)}` : ""}`} value={tm ? fmtCurCompact(tm.actual_inr, "INR") : "—"}
            sub={tm ? ccyLine(tm.actual_by_ccy) : ""} accent="#0369A1" icon={Receipt} />
          <Stat testid="sales-stat-this-month-expected" label={`This Month · Expected${curMonth ? ` · ${monthLabel(curMonth)}` : ""}`} value={tm ? fmtCurCompact(tm.expected_inr, "INR") : "—"}
            sub={`by target dispatch · ${ccyLine(tm?.expected_by_ccy)}`} accent="#9333EA" icon={Target} />
          {tm && tm.spillover_inr > 0 && (
            <Stat testid="sales-stat-this-month-spillover" label={`Spillover into ${curMonth ? monthLabel(curMonth) : "this month"}`}
              value={fmtCurCompact(tm.spillover_inr, "INR")}
              sub={`overdue backlog target (fixed) · ${tm.spillover_sale_inr > 0 ? `${fmtCurCompact(tm.spillover_sale_inr, "INR")} cleared` : "0 cleared"} · ${ccyLine(tm.spillover_by_ccy)}`} accent="#F59E0B" icon={Warning} />
          )}
          <Stat label="Achievement" value={achievement != null ? `${achievement}%` : "—"}
            sub="actual vs expected (₹)" accent={achievement != null && achievement >= 100 ? "#059669" : "#EA580C"} icon={TrendUp} />
        </div>

        <div className="jde-panel" data-testid="sales-chart-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><Receipt size={13} weight="bold" /> Monthly sales — Projected (target dispatch) vs Actual (GST invoice), ₹ per month</div>
            <div data-testid="sales-pricebasis" style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <button data-testid="sales-spillover-toggle" onClick={() => setSpill((s) => !s)}
                title="Roll each overdue-unshipped PO's open balance forward into the current month (opt-in; off = original plan)"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                  border: `1px solid ${spill ? "#F59E0B" : "#CBD5E1"}`, borderRadius: 999,
                  padding: "4px 12px", fontSize: 12, fontWeight: 700,
                  background: spill ? "#F59E0B" : "#fff", color: spill ? "#fff" : "#334155",
                }}>
                <Warning size={13} weight="bold" /> Spillover {spill ? "ON" : "OFF"}
              </button>
              {spill && (
                <button data-testid="sales-backlog-toggle" onClick={() => setShowBacklog((s) => !s)}
                  title="Show every overdue-unshipped PO line that rolls forward as spillover"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                    border: "1px solid #B45309", borderRadius: 999, padding: "4px 12px",
                    fontSize: 12, fontWeight: 700, background: showBacklog ? "#B45309" : "#fff",
                    color: showBacklog ? "#fff" : "#B45309",
                  }}>
                  {showBacklog ? "Hide" : "View"} Overdue Backlog
                </button>
              )}
              <span className="muted" style={{ fontSize: 11 }}>Price</span>
              <div style={{ display: "inline-flex", border: "1px solid #CBD5E1", borderRadius: 999, overflow: "hidden" }}>
                {[["invoice", "Invoice"], ["po", "PO"]].map(([v, lbl]) => (
                  <button key={v} data-testid={`sales-basis-${v}`} onClick={() => setPriceBasis(v)}
                    style={{
                      border: "none", cursor: "pointer", padding: "4px 12px", fontSize: 12, fontWeight: 700,
                      background: priceBasis === v ? "#0369A1" : "#fff",
                      color: priceBasis === v ? "#fff" : "#334155",
                    }}>{lbl}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: "12px 8px 4px" }}>
            {chart.length === 0
              ? <div className="muted" style={{ padding: 24, textAlign: "center" }}>No sales data in range.</div>
              : (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={chart} margin={{ top: 8, right: 16, left: 8, bottom: 4 }} barGap={2}>
                    <CartesianGrid vertical={false} stroke="#EEF1F5" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#334155" }} interval={0} angle={-30} textAnchor="end" height={54} />
                    <YAxis tick={{ fontSize: 10.5, fill: "#64748B" }} tickFormatter={(v) => fmtCurCompact(v, "INR")} width={70} />
                    <Tooltip content={<SalesChartTooltip />} cursor={{ fill: "rgba(3,105,161,0.06)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {curMonth && <ReferenceLine x={monthLabel(curMonth)} stroke="#DC2626" strokeDasharray="3 3" label={{ value: "now", fontSize: 10, fill: "#DC2626" }} />}
                    <Bar dataKey="Expected" stackId="plan" fill="#C4B5FD" radius={[0, 0, 0, 0]} cursor="pointer"
                         onClick={(d) => openMonth(d?.payload?.raw)} />
                    <Bar dataKey="Spillover" stackId="plan" name="Spillover (backlog)" fill="#F59E0B" radius={[4, 4, 0, 0]} cursor="pointer"
                         onClick={(d) => openMonth(d?.payload?.raw)} />
                    <Bar dataKey="CurrentSale" stackId="act" name="Current-month sale" fill="#0369A1" radius={[0, 0, 0, 0]} cursor="pointer"
                         onClick={(d) => openMonth(d?.payload?.raw)} />
                    <Bar dataKey="SpilloverSale" stackId="act" name="Spillover sale (backlog cleared)" fill="#0E7490" radius={[4, 4, 0, 0]} cursor="pointer"
                         onClick={(d) => d?.payload?.SpilloverSale > 0 ? openListDrill("spillover", d?.payload?.raw) : openMonth(d?.payload?.raw)} />
                  </BarChart>
                </ResponsiveContainer>
              )}
          </div>
        </div>

        {spill && showBacklog && (
          <div className="jde-panel" data-testid="sales-overdue-backlog" style={{ marginTop: 16, border: "1px solid #FCD34D" }}>
            <div className="jde-panel-header" style={{ background: "#FFFBEB" }}>
              <div className="jde-panel-title" style={{ color: "#B45309" }}>
                <Warning size={14} weight="bold" style={{ display: "inline", marginRight: 6 }} />
                Overdue backlog · rolls forward as spillover
                {backlog && <span className="muted" style={{ fontWeight: 400, fontSize: 11, marginLeft: 8 }}>{backlog.count} PO lines · {fmtCur(backlog.total_open_value_inr, "INR")} open</span>}
              </div>
            </div>
            <div className="jde-panel-body" style={{ padding: 0, maxHeight: 420, overflow: "auto" }}>
              {!backlog ? <div style={{ padding: 20 }} className="muted">Loading…</div> : (
                <table className="jde-grid">
                  <thead><tr>
                    <th style={bcell}>Customer</th>
                    <th style={bcell}>PO</th>
                    <th style={bcell}>Part</th>
                    <th style={bcell}>Target date</th>
                    <th style={{ ...bcell, textAlign: "right" }}>Days overdue</th>
                    <th style={{ ...bcell, textAlign: "right" }}>Open qty</th>
                    <th style={{ ...bcell, textAlign: "right" }}>Open value (₹)</th>
                  </tr></thead>
                  <tbody>
                    {backlog.rows.map((r, i) => (
                      <tr key={i} data-testid={`backlog-row-${i}`}>
                        <td style={{ ...bcell, fontWeight: 600 }}>{r.customer_name || "—"}</td>
                        <td style={{ ...bcell, fontFamily: "Inter, sans-serif" }}>{r.ms_custpo || r.ms_pono || "—"}</td>
                        <td style={{ ...bcell, fontFamily: "Inter, sans-serif" }} title={r.name}>{r.part || "—"}</td>
                        <td style={{ ...bcell, fontFamily: "Inter, sans-serif" }}>{fmtDate(r.target_date)}</td>
                        <td style={{ ...brt, fontWeight: 800, color: r.days_overdue > 180 ? "#DC2626" : "#B45309" }}>{r.days_overdue != null ? `${r.days_overdue}d` : "—"}</td>
                        <td style={brt}>{fmtNum(r.open_qty)}</td>
                        <td style={brt} title={`${r.open_qty} × ${r.price} ${r.currency}`}>{fmtCurCompact(r.open_value_inr, "INR")}</td>
                      </tr>
                    ))}
                    {backlog.rows.length === 0 && <tr><td colSpan={7} className="muted" style={{ padding: 20, textAlign: "center" }}>No overdue-unshipped POs.</td></tr>}
                  </tbody>
                  {backlog.rows.length > 0 && (
                    <tfoot><tr>
                      <td style={bftl} colSpan={5}>Total · {backlog.count} overdue PO lines</td>
                      <td style={bft}></td>
                      <td style={bft} title={fmtCur(backlog.total_open_value_inr, "INR")}>{fmtCurCompact(backlog.total_open_value_inr, "INR")}</td>
                    </tr></tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        )}


        <div className="jde-panel" data-testid="sales-table" style={{ marginTop: 16 }}>
          <div className="jde-panel-header"><div className="jde-panel-title">Monthly detail <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>· click a month to drill into customer &amp; part sales</span></div></div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            <table className="jde-grid">
              <thead><tr>
                <th>Month</th>
                <th style={{ textAlign: "right" }}>Expected (₹)</th>
                <th>Expected mix</th>
                <th style={{ textAlign: "right" }}>Actual (₹)</th>
                <th>Actual split</th>
                <th style={{ textAlign: "right" }}>CFS shift</th>
              </tr></thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.month} data-testid={`sales-row-${s.month}`}
                    onClick={() => openMonth(s.month)}
                    style={{ cursor: "pointer", background: s.month === curMonth ? "rgba(3,105,161,0.06)" : undefined }}>
                    <td style={{ fontWeight: 700, color: "var(--omh-primary)" }}>{monthLabel(s.month)}{s.month === curMonth ? " • now" : ""}</td>
                    <td className="text-mono" style={{ textAlign: "right" }} title={fmtCur(s.expected_inr, "INR")}>
                      {fmtCurCompact(s.expected_inr, "INR")}
                      {s.spillover_inr > 0 && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#B45309" }} title={`Spillover backlog target (fixed; does not shrink as sales post): ${fmtCur(s.spillover_inr, "INR")}`}>
                          +{fmtCurCompact(s.spillover_inr, "INR")} spillover
                        </div>
                      )}
                    </td>
                    <td className="muted" style={{ fontSize: 12 }}>{ccyLine(s.expected_by_ccy)}</td>
                    <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }} title={fmtCur(s.actual_inr, "INR")}>{fmtCurCompact(s.actual_inr, "INR")}</td>
                    <td style={{ fontSize: 12 }} data-testid={`sales-actual-split-${s.month}`}>
                      {(s.spillover_sale_inr > 0 || s.current_sale_inr > 0) ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <span style={{ color: "#0369A1" }} title="Current-month sale (this month's own plan shipped)">Current {fmtCurCompact(s.current_sale_inr || 0, "INR")}</span>
                          {s.spillover_sale_inr > 0 && <span role="button" data-testid={`sales-spillover-sale-link-${s.month}`}
                            onClick={(e) => { e.stopPropagation(); openListDrill("spillover", s.month); }}
                            style={{ color: "#0E7490", fontWeight: 700, cursor: "pointer", textDecoration: "underline dotted" }}
                            title="Click to see which overdue POs were cleared this month">Spillover {fmtCurCompact(s.spillover_sale_inr, "INR")}</span>}
                        </div>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="text-mono" style={{ textAlign: "right", fontSize: 12 }} data-testid={`sales-cfs-shift-${s.month}`}>
                      {(s.cfs_shift_out_inr > 0 || s.cfs_shift_in_inr > 0) ? (
                        <span role="button" data-testid={`sales-cfs-shift-link-${s.month}`}
                          onClick={(e) => { e.stopPropagation(); openListDrill("cfs", s.month); }}
                          style={{ cursor: "pointer" }} title="Click to see which POs moved (planned vs CFS ship-by month)">
                          {s.cfs_shift_out_inr > 0 && <span style={{ color: "#B45309", fontWeight: 700, textDecoration: "underline dotted" }}>↦ {fmtCurCompact(s.cfs_shift_out_inr, "INR")}</span>}
                          {s.cfs_shift_in_inr > 0 && <span style={{ color: "#0f766e", display: "block", textDecoration: "underline dotted" }}>↤ {fmtCurCompact(s.cfs_shift_in_inr, "INR")}</span>}
                        </span>
                      ) : <span className="muted">—</span>}
                    </td>
                  </tr>
                ))}
                {series.length === 0 && <tr><td colSpan={6} className="muted" style={{ padding: 18, textAlign: "center" }}>No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {drillMonth && (
        <SalesDrillModal
          month={drillMonth} monthLabel={monthLabel(drillMonth)}
          customer={drillCustomer} custRows={custRows} partRows={partRows}
          part={drillPart} partDetail={partDetail} spill={spill}
          loading={drillLoading} onPickCustomer={openCustomer} onPickPart={openPart}
          partsView={partsView} onToggleView={togglePartsView}
          onBack={() => { setDrillCustomer(null); setPartRows(null); setDrillPart(null); setPartDetail(null); }}
          onBackToParts={() => { setDrillPart(null); setPartDetail(null); }}
          onClose={closeDrill} />
      )}
      {listDrill && (
        <SalesListModal kind={listDrill.kind} month={listDrill.month}
          data={listData} loading={listLoading} onClose={() => { setListDrill(null); setListData(null); }} />
      )}
    </Layout>
  );
}

function SalesListModal({ kind, month, data, loading, onClose }) {
  const isSpill = kind === "spillover";
  const rows = data?.rows || [];
  const title = isSpill ? `Spillover sale — overdue backlog cleared in ${monthLabel(month)}`
                        : `CFS shift — POs moved (${monthLabel(month)})`;
  const subtitle = isSpill
    ? (data ? `${data.count} line(s) · ${fmtCur(data.total_inr || 0, "INR")} cleared` : "")
    : (data ? `${data.count} line(s) · ↦ ${fmtCur(data.shifted_out_inr || 0, "INR")} out · ↤ ${fmtCur(data.shifted_in_inr || 0, "INR")} in` : "");
  return (
    <div data-testid="sales-list-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, width: "min(920px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>{title}</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 3 }}>{subtitle}</div>
          </div>
          <button data-testid="sales-list-close" onClick={onClose}
            style={{ border: "none", background: "#f1f5f9", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, color: "#475569" }}>×</button>
        </div>
        <div style={{ overflowY: "auto", padding: "6px 20px 18px" }}>
          {loading ? <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading…</div>
            : rows.length === 0 ? <div className="muted" style={{ padding: 24, textAlign: "center" }}>No matching lines.</div>
            : (
              <table className="jde-table" style={{ width: "100%", fontSize: 12.5 }}>
                <thead><tr>
                  <th>Customer PO</th><th>Part</th>
                  <th style={{ textAlign: "right" }}>Qty</th>
                  <th style={{ textAlign: "right" }}>Value</th>
                  <th style={{ textAlign: "right" }}>Value (₹)</th>
                  {isSpill ? <><th>Inv #</th><th>Orig plan</th><th>CFS ship-by</th></>
                           : <><th>Planned month</th><th>CFS ship-by</th><th>Dir</th></>}
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`sales-list-row-${i}`}>
                      <td style={{ fontWeight: 600 }}>{r.custpo}</td>
                      <td className="text-mono" title={r.iname}>{r.part}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(r.qty)}</td>
                      <td className="text-mono" style={{ textAlign: "right" }}>{fmtCur(r.value, r.ccy)}</td>
                      <td className="text-mono" style={{ textAlign: "right", fontWeight: 700 }}>{fmtCur(r.value_inr, "INR")}</td>
                      {isSpill ? <><td className="muted">{r.inv_no}</td><td className="muted">{r.orig_planned_month ? monthLabel(r.orig_planned_month) : "—"}</td><td style={{ color: r.cfs_month ? "#B45309" : "#94a3b8", fontWeight: r.cfs_month ? 600 : 400 }}>{r.cfs_month ? monthLabel(r.cfs_month) : "—"}</td></>
                               : <><td className="muted">{monthLabel(r.planned_month)}</td><td style={{ color: "#B45309", fontWeight: 600 }}>{monthLabel(r.cfs_month)}</td>
                                   <td>{r.direction === "out" ? <span style={{ color: "#B45309" }}>↦ out</span> : <span style={{ color: "#0f766e" }}>↤ in</span>}</td></>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function SalesDrillModal({ month, monthLabel, customer, custRows, partRows, part, partDetail, spill, loading, onPickCustomer, onPickPart, partsView, onToggleView, onBack, onBackToParts, onClose }) {
  // Live item filter (by part # or description) for the customer→parts view. Totals below the
  // list recompute from the filtered rows so the footer always reflects what's on screen.
  const [partQuery, setPartQuery] = useState("");
  useEffect(() => { setPartQuery(""); }, [customer, part, partsView]);
  const shownParts = useMemo(() => {
    const q = partQuery.trim().toLowerCase();
    const rows = partRows || [];
    if (!q) return rows;
    return rows.filter((p) =>
      String(p.part_no || "").toLowerCase().includes(q) ||
      String(p.name || "").toLowerCase().includes(q));
  }, [partRows, partQuery]);
  const hasCarry = (partRows || []).some((p) => (Number(p.carryover_qty) || 0) > 0);
  const cell = { padding: "7px 9px", fontSize: 12.5, whiteSpace: "normal", wordBreak: "break-word" };
  const rt = { ...cell, textAlign: "right", fontFamily: "Inter, sans-serif" };
  const ft = { ...rt, fontWeight: 800, borderTop: "2px solid var(--omh-border)", background: "#f8fafc" };
  const ftl = { ...cell, fontWeight: 800, borderTop: "2px solid var(--omh-border)", background: "#f8fafc" };
  const gridStyle = { width: "100%", tableLayout: "fixed" };
  const sum = (arr, k) => (arr || []).reduce((a, x) => a + (Number(x[k]) || 0), 0);
  const cur0 = (v) => (v ? fmtCurCompact(v, "INR") : "—");
  const ordCcy = (partDetail?.orders?.[0]?.currency) || "INR";
  const invCcy = (partDetail?.invoices?.[0]?.currency) || "INR";
  const partCcy = (partRows?.[0]?.currency) || "INR";
  return (
    <div data-testid="sales-drill-modal"
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000,
        display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "5vh 16px", overflow: "auto" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, width: "min(1280px, 97vw)", boxShadow: "0 24px 60px rgba(0,0,0,.3)",
          display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          padding: "16px 20px", borderBottom: "1px solid var(--omh-border)" }}>
          <div style={{ fontSize: 15, fontWeight: 800, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Receipt size={18} weight="duotone" color="#0369A1" />
            <span data-testid="drill-crumb-month" onClick={onBack}
              style={{ cursor: customer ? "pointer" : "default", color: customer ? "var(--omh-primary)" : "inherit" }}>
              Sales · {monthLabel}
            </span>
            {customer && <><CaretRight size={13} />
              <span data-testid="drill-crumb-customer" onClick={part ? onBackToParts : undefined}
                style={{ cursor: part ? "pointer" : "default", color: part ? "var(--omh-primary)" : "inherit" }}>{customer}</span></>}
            {part && <><CaretRight size={13} /><span data-testid="drill-crumb-part">{part}</span></>}
          </div>
          <button data-testid="drill-close" onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}>
            <X size={20} weight="bold" />
          </button>
        </div>

        <div style={{ overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0 }} data-testid="drill-scroll-body">
          {loading && <div className="muted" style={{ padding: 28, textAlign: "center" }} data-testid="drill-loading">Loading…</div>}

          {!loading && !customer && (
            <table className="jde-grid" data-testid="drill-customers-table" style={gridStyle}>
              <thead><tr>
                <th style={cell}>Customer</th>
                <th style={{ ...cell, textAlign: "right" }}>Expected (₹)</th>
                {spill && <th style={{ ...cell, textAlign: "right", color: "#B45309" }}>Spillover (₹)</th>}
                <th style={{ ...cell, textAlign: "right" }}>Actual (₹)</th>
                <th style={{ ...cell, width: 30 }}></th>
              </tr></thead>
              <tbody>
                {(custRows || []).map((c) => (
                  <tr key={c.customer_name} data-testid={`drill-cust-${c.customer_name}`}
                    onClick={() => onPickCustomer(c.customer_name)} style={{ cursor: "pointer" }}>
                    <td style={{ ...cell, fontWeight: 700, color: "var(--omh-primary)" }}>{c.customer_name}</td>
                    <td style={rt} title={fmtCur(c.expected_inr, "INR")}>{c.expected_inr ? fmtCurCompact(c.expected_inr, "INR") : <span className="muted">—</span>}</td>
                    {spill && <td style={{ ...rt, fontWeight: 700, color: "#B45309" }} title={c.spillover_inr ? `Overdue-unshipped rolled into this month: ${fmtCur(c.spillover_inr, "INR")}` : ""}>{c.spillover_inr ? fmtCurCompact(c.spillover_inr, "INR") : <span className="muted">—</span>}</td>}
                    <td style={{ ...rt, fontWeight: 700 }} title={fmtCur(c.actual_inr, "INR")}>{c.actual_inr ? fmtCurCompact(c.actual_inr, "INR") : <span className="muted">—</span>}</td>
                    <td style={{ ...cell, color: "#94a3b8" }}><CaretRight size={13} /></td>
                  </tr>
                ))}
                {(custRows || []).length === 0 && <tr><td colSpan={spill ? 5 : 4} className="muted" style={{ padding: 20, textAlign: "center" }}>No sales for this month.</td></tr>}
              </tbody>
              {(custRows || []).length > 0 && (
                <tfoot><tr data-testid="drill-cust-totals">
                  <td style={ftl}>Total · {custRows.length} customers</td>
                  <td style={ft} title={fmtCur(sum(custRows, "expected_inr"), "INR")}>{cur0(sum(custRows, "expected_inr"))}</td>
                  {spill && <td style={{ ...ft, color: "#B45309" }} title={fmtCur(sum(custRows, "spillover_inr"), "INR")}>{sum(custRows, "spillover_inr") ? cur0(sum(custRows, "spillover_inr")) : ""}</td>}
                  <td style={ft} title={fmtCur(sum(custRows, "actual_inr"), "INR")}>{cur0(sum(custRows, "actual_inr"))}</td>
                  <td style={ft}></td>
                </tr></tfoot>
              )}
            </table>
          )}

          {!loading && customer && !part && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 4px", flexWrap: "wrap", gap: 8 }}>
                <button data-testid="drill-back" onClick={onBack}
                  style={{ background: "transparent", border: "none",
                    color: "var(--omh-primary)", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
                  ← Back to customers
                </button>
                <div style={{ display: "inline-flex", border: "1px solid var(--omh-border)", borderRadius: 8, overflow: "hidden" }} data-testid="parts-view-toggle">
                  {[["revenue", "Revenue (invoiced)"], ["fulfilment", "Fulfilment (Planned/Shipped/Open)"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`parts-view-${v}`} onClick={() => onToggleView(v)}
                      style={{ padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", border: "none",
                        background: partsView === v ? "var(--omh-primary)" : "#fff",
                        color: partsView === v ? "#fff" : "#475569" }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ padding: "8px 14px 4px" }}>
                <div style={{ position: "relative", maxWidth: 420 }}>
                  <MagnifyingGlass size={15} weight="bold"
                    style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                  <input
                    data-testid="sales-drill-part-search"
                    value={partQuery}
                    onChange={(e) => setPartQuery(e.target.value)}
                    placeholder="Filter by item # or description…"
                    style={{ width: "100%", padding: "8px 30px 8px 32px", fontSize: 12.5,
                      border: "1px solid var(--omh-border)", borderRadius: 8, outline: "none" }} />
                  {partQuery && (
                    <button data-testid="sales-drill-part-search-clear" onClick={() => setPartQuery("")}
                      aria-label="Clear search"
                      style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                        background: "transparent", border: "none", cursor: "pointer", color: "#64748b", display: "flex" }}>
                      <X size={14} weight="bold" />
                    </button>
                  )}
                </div>
                {partQuery && (
                  <div data-testid="sales-drill-part-search-count" className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                    Showing {shownParts.length} of {(partRows || []).length} parts
                  </div>
                )}
              </div>
              <div style={{ padding: "0 14px 6px", fontSize: 11.5 }} className="muted">
                {partsView === "fulfilment"
                  ? (<>Orders whose TARGET DISPATCH falls in this month. Planned = qty due this month · Shipped = ALL of this part dispatched this month (any PO, incl. carry-over orders) · Open = remaining plan balance. (Shipped is true monthly throughput, so Planned = Shipped + Open may not tie exactly.){hasCarry ? <span style={{ color: "#B45309", fontWeight: 600 }}> Spillover ON — overdue backlog from earlier months that hasn't shipped is carried into this month (tagged “carry-over”).</span> : null}</>)
                  : "EXPECTED = open order balance due this month · ACTUAL = invoiced this month (shipped orders move from Expected → Actual)."}
              </div>
              {partsView === "fulfilment" ? (
                <table className="jde-grid" data-testid="drill-parts-table" style={gridStyle}>
                  <thead><tr>
                    <th style={cell}>Part</th>
                    <th style={cell}>Description</th>
                    <th style={{ ...cell, textAlign: "right" }}>Planned Qty</th>
                    <th style={{ ...cell, textAlign: "right" }}>Shipped Qty</th>
                    <th style={{ ...cell, textAlign: "right" }}>Open Qty</th>
                    <th style={{ ...cell, textAlign: "right" }}>Planned ({partCcy})</th>
                    <th style={{ ...cell, textAlign: "right" }}>Shipped ({partCcy})</th>
                    <th style={{ ...cell, textAlign: "right" }}>Open ({partCcy})</th>
                    <th style={{ ...cell, textAlign: "right" }}>Planned (₹)</th>
                    <th style={{ ...cell, width: 30 }}></th>
                  </tr></thead>
                  <tbody>
                    {shownParts.map((p) => {
                      const pct = p.planned_qty ? Math.round((p.shipped_qty / p.planned_qty) * 100) : 0;
                      return (
                        <tr key={p.part_no} data-testid={`drill-part-${p.part_no}`}
                          onClick={() => onPickPart(p.part_no)} style={{ cursor: "pointer" }}>
                          <td style={{ ...cell, fontWeight: 700, fontFamily: "Inter, sans-serif", color: "var(--omh-primary)" }}>{p.part_no}<WmPart icode={p.part_no} /></td>
                          <td style={{ ...cell, fontSize: 12 }}>{p.name || <span className="muted">—</span>}</td>
                          <td style={rt}>{fmtNum(p.planned_qty)}{p.carryover_qty > 0 ? <span data-testid={`drill-carryover-${p.part_no}`} title={`Includes ${fmtNum(p.carryover_qty)} carried over from earlier months (overdue, still open)`} style={{ display: "inline-block", marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#B45309", background: "#FEF3C7", borderRadius: 4, padding: "1px 5px", verticalAlign: "middle", fontFamily: "system-ui" }}>+{fmtNum(p.carryover_qty)} carry-over</span> : null}</td>
                          <td style={{ ...rt, color: "#16A34A" }}>{fmtNum(p.shipped_qty)} <span className="muted" style={{ fontSize: 10 }}>({pct}%)</span></td>
                          <td style={{ ...rt, color: p.open_qty ? "#EA580C" : "#94a3b8" }}>{p.open_qty ? fmtNum(p.open_qty) : "—"}</td>
                          <td style={rt} title={fmtCur(p.planned_native, p.currency)}>{p.planned_native ? fmtCurCompact(p.planned_native, p.currency) : "—"}</td>
                          <td style={rt} title={fmtCur(p.shipped_native, p.currency)}>{p.shipped_native ? fmtCurCompact(p.shipped_native, p.currency) : "—"}</td>
                          <td style={rt} title={fmtCur(p.open_native, p.currency)}>{p.open_native ? fmtCurCompact(p.open_native, p.currency) : "—"}</td>
                          <td style={{ ...rt, fontWeight: 700 }} title={fmtCur(p.planned_inr, "INR")}>{p.planned_inr ? fmtCurCompact(p.planned_inr, "INR") : "—"}</td>
                          <td style={{ ...cell, color: "#94a3b8" }}><CaretRight size={13} /></td>
                        </tr>
                      );
                    })}
                    {shownParts.length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 20, textAlign: "center" }}>{partQuery ? "No items match your search." : "No orders due for dispatch this month."}</td></tr>}
                  </tbody>
                  {shownParts.length > 0 && (
                    <tfoot><tr data-testid="drill-parts-totals">
                      <td style={ftl} colSpan={2}>Total · {shownParts.length} parts</td>
                      <td style={ft}>{fmtNum(sum(shownParts, "planned_qty"))}</td>
                      <td style={{ ...ft, color: "#16A34A" }}>{fmtNum(sum(shownParts, "shipped_qty"))}</td>
                      <td style={{ ...ft, color: "#EA580C" }}>{fmtNum(sum(shownParts, "open_qty"))}</td>
                      <td style={ft} title={fmtCur(sum(shownParts, "planned_native"), partCcy)}>{fmtCurCompact(sum(shownParts, "planned_native"), partCcy)}</td>
                      <td style={ft} title={fmtCur(sum(shownParts, "shipped_native"), partCcy)}>{fmtCurCompact(sum(shownParts, "shipped_native"), partCcy)}</td>
                      <td style={ft} title={fmtCur(sum(shownParts, "open_native"), partCcy)}>{fmtCurCompact(sum(shownParts, "open_native"), partCcy)}</td>
                      <td style={ft} title={fmtCur(sum(shownParts, "planned_inr"), "INR")}>{cur0(sum(shownParts, "planned_inr"))}</td>
                      <td style={ft}></td>
                    </tr></tfoot>
                  )}
                </table>
              ) : (
              <table className="jde-grid" data-testid="drill-parts-table" style={gridStyle}>
                <thead><tr>
                  <th style={cell}>Part</th>
                  <th style={cell}>Description</th>
                  <th style={{ ...cell, textAlign: "right" }}>Plan Qty</th>
                  <th style={{ ...cell, textAlign: "right" }}>Unit Price</th>
                  <th style={{ ...cell, textAlign: "right" }}>Expected ({partCcy})</th>
                  <th style={{ ...cell, textAlign: "right" }}>Expected (₹)</th>
                  <th style={{ ...cell, textAlign: "right" }}>Shipped Qty</th>
                  <th style={{ ...cell, textAlign: "right" }}>Actual ({partCcy})</th>
                  <th style={{ ...cell, textAlign: "right" }}>Actual (₹)</th>
                  <th style={{ ...cell, width: 30 }}></th>
                </tr></thead>
                <tbody>
                  {shownParts.map((p) => (
                    <tr key={p.part_no} data-testid={`drill-part-${p.part_no}`}
                      onClick={() => onPickPart(p.part_no)} style={{ cursor: "pointer" }}>
                      <td style={{ ...cell, fontWeight: 700, fontFamily: "Inter, sans-serif", color: "var(--omh-primary)" }}>
                        {p.part_no}
                        {p.spillover_qty > 0 && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, color: "#fff", background: "#F59E0B", borderRadius: 4, padding: "1px 5px", fontFamily: "var(--omh-font, sans-serif)" }} title="Includes overdue-unshipped rolled forward into this month">SPILLOVER</span>}
                      </td>
                      <td style={{ ...cell, fontSize: 12 }}>{p.name || <span className="muted">—</span>}</td>
                      <td style={rt}>
                        {p.expected_qty ? fmtNum(p.expected_qty) : <span className="muted">—</span>}
                        {p.spillover_qty > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "#B45309" }}>+{fmtNum(p.spillover_qty)} spill</div>}
                      </td>
                      <td style={rt}>{p.price ? fmtRate(p.price, p.currency) : <span className="muted">—</span>}</td>
                      <td style={rt} title={fmtCur(p.expected_native, p.currency)}>{p.expected_native ? fmtCurCompact(p.expected_native, p.currency) : <span className="muted">—</span>}</td>
                      <td style={rt} title={fmtCur(p.expected_inr, "INR")}>
                        {p.expected_inr ? fmtCurCompact(p.expected_inr, "INR") : <span className="muted">—</span>}
                        {p.spillover_inr > 0 && <div style={{ fontSize: 11, fontWeight: 700, color: "#B45309" }} title={`Spillover: ${fmtCur(p.spillover_inr, "INR")}`}>+{fmtCurCompact(p.spillover_inr, "INR")}</div>}
                      </td>
                      <td style={rt}>{p.actual_qty ? fmtNum(p.actual_qty) : <span className="muted">—</span>}</td>
                      <td style={rt} title={fmtCur(p.actual_native, p.currency)}>{p.actual_native ? fmtCurCompact(p.actual_native, p.currency) : <span className="muted">—</span>}</td>
                      <td style={{ ...rt, fontWeight: 700 }} title={fmtCur(p.actual_inr, "INR")}>{p.actual_inr ? fmtCurCompact(p.actual_inr, "INR") : <span className="muted">—</span>}</td>
                      <td style={{ ...cell, color: "#94a3b8" }}><CaretRight size={13} /></td>
                    </tr>
                  ))}
                  {shownParts.length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 20, textAlign: "center" }}>{partQuery ? "No items match your search." : "No parts for this customer/month."}</td></tr>}
                </tbody>
                {shownParts.length > 0 && (
                  <tfoot><tr data-testid="drill-parts-totals">
                    <td style={ftl} colSpan={2}>Total · {shownParts.length} parts</td>
                    <td style={ft}>{fmtNum(sum(shownParts, "expected_qty"))}</td>
                    <td style={ft}></td>
                    <td style={ft} title={fmtCur(sum(shownParts, "expected_native"), partCcy)}>{fmtCurCompact(sum(shownParts, "expected_native"), partCcy)}</td>
                    <td style={ft} title={fmtCur(sum(shownParts, "expected_inr"), "INR")}>{cur0(sum(shownParts, "expected_inr"))}</td>
                    <td style={ft}>{fmtNum(sum(shownParts, "actual_qty"))}</td>
                    <td style={ft} title={fmtCur(sum(shownParts, "actual_native"), partCcy)}>{fmtCurCompact(sum(shownParts, "actual_native"), partCcy)}</td>
                    <td style={ft} title={fmtCur(sum(shownParts, "actual_inr"), "INR")}>{cur0(sum(shownParts, "actual_inr"))}</td>
                    <td style={ft}></td>
                  </tr></tfoot>
                )}
              </table>
              )}
            </>
          )}

          {!loading && part && partDetail && (
            <div style={{ padding: "6px 0 12px" }}>
              <button data-testid="drill-back-parts" onClick={onBackToParts}
                style={{ margin: "10px 0 4px 14px", background: "transparent", border: "none",
                  color: "var(--omh-primary)", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
                ← Back to parts
              </button>

              {partsView === "fulfilment" ? (
                <>
                  <div style={{ padding: "6px 14px 4px", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#7c3aed" }}>Dispatch plan for this month · Shipped = all dispatches of this part this month</div>
                  <table className="jde-grid" data-testid="drill-part-fulfilment" style={gridStyle}>
                    <thead><tr>
                      <th style={cell}>ERP PO#</th><th style={cell}>Customer PO</th><th style={cell}>End-Cust PO</th>
                      <th style={cell}>Target dispatch</th><th style={cell}>Due date</th>
                      <th style={{ ...cell, textAlign: "right" }}>Planned Qty</th>
                      <th style={{ ...cell, textAlign: "right" }}>Shipped Qty</th>
                      <th style={{ ...cell, textAlign: "right" }}>Open Qty</th>
                      <th style={{ ...cell, textAlign: "right" }}>Planned ({ordCcy})</th>
                      <th style={{ ...cell, textAlign: "right" }}>Planned (₹)</th>
                    </tr></thead>
                    <tbody>
                      {(partDetail.orders || []).map((o, i) => {
                        const pct = o.planned_qty ? Math.round((o.shipped_qty / o.planned_qty) * 100) : 0;
                        return (
                          <tr key={`f${i}`} style={o._other ? { background: "#faf5ff", fontStyle: "italic" } : undefined}>
                            <td style={{ ...cell, fontFamily: "Inter, sans-serif" }}>{o.ms_pono || (o._other ? "" : "—")}</td>
                            <td style={{ ...cell, fontFamily: o._other ? "inherit" : "var(--jde-font-mono, monospace)", fontSize: o._other ? 11 : undefined, color: o._other ? "#7c3aed" : undefined }} colSpan={o._other ? 4 : 1}>{o.ms_custpo || "—"}</td>
                            {!o._other && <td style={{ ...cell, fontFamily: "Inter, sans-serif", fontSize: 11 }}>{o.cust_po2 || <span className="muted">—</span>}</td>}
                            {!o._other && <td style={{ ...cell, fontFamily: "Inter, sans-serif", fontWeight: 700 }}>{fmtDate(o.target_date)}</td>}
                            {!o._other && <td style={{ ...cell, fontFamily: "Inter, sans-serif", color: "#64748b" }}>{fmtDate(o.deliv_date)}</td>}
                            <td style={rt}>{o.planned_qty ? fmtNum(o.planned_qty) : "—"}</td>
                            <td style={{ ...rt, color: "#16A34A" }}>{fmtNum(o.shipped_qty)}{o.planned_qty ? <span className="muted" style={{ fontSize: 10 }}> ({pct}%)</span> : null}</td>
                            <td style={{ ...rt, color: o.open_qty ? "#EA580C" : "#94a3b8" }}>
                              {o.open_qty ? fmtNum(o.open_qty) : "—"}
                              {o.shipped_other_qty > 0 ? <div className="muted" style={{ fontSize: 9.5, fontWeight: 500, color: "#0E7490" }} title="These units were dispatched in a different month (not this month), so nothing remains open here.">↳ {fmtNum(o.shipped_other_qty)} shipped in another month</div> : null}
                            </td>
                            <td style={rt} title={fmtCur(o.planned_native, o.currency)}>{o.planned_native ? fmtCurCompact(o.planned_native, o.currency) : "—"}</td>
                            <td style={{ ...rt, fontWeight: 700 }} title={fmtCur(o.planned_inr, "INR")}>{o.planned_inr ? fmtCurCompact(o.planned_inr, "INR") : "—"}</td>
                          </tr>
                        );
                      })}
                      {(partDetail.orders || []).length === 0 && <tr><td colSpan={10} className="muted" style={{ padding: 16, textAlign: "center" }}>No orders due for dispatch this month.</td></tr>}
                    </tbody>
                    {(partDetail.orders || []).length > 0 && (
                      <tfoot><tr data-testid="drill-fulfilment-totals">
                        <td style={ftl} colSpan={5}>Total · {partDetail.orders.length} orders</td>
                        <td style={ft}>{fmtNum(sum(partDetail.orders, "planned_qty"))}</td>
                        <td style={{ ...ft, color: "#16A34A" }}>{fmtNum(sum(partDetail.orders, "shipped_qty"))}</td>
                        <td style={{ ...ft, color: "#EA580C" }}>{fmtNum(sum(partDetail.orders, "open_qty"))}</td>
                        <td style={ft} title={fmtCur(sum(partDetail.orders, "planned_native"), ordCcy)}>{fmtCurCompact(sum(partDetail.orders, "planned_native"), ordCcy)}</td>
                        <td style={ft} title={fmtCur(sum(partDetail.orders, "planned_inr"), "INR")}>{cur0(sum(partDetail.orders, "planned_inr"))}</td>
                      </tr></tfoot>
                    )}
                  </table>
                </>
              ) : (
              <>
              <div style={{ padding: "6px 14px 4px", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#7c3aed" }}>Orders (plan · full ordered value)</div>
              <table className="jde-grid" data-testid="drill-part-orders" style={gridStyle}>
                <thead><tr>
                  <th style={cell}>ERP PO#</th><th style={cell}>Customer PO</th><th style={cell}>End-Cust PO</th>
                  <th style={cell}>Target dispatch</th><th style={cell}>Due date</th>
                  <th style={{ ...cell, textAlign: "right" }}>Order Qty</th>
                  <th style={{ ...cell, textAlign: "right" }}>Unit Price</th>
                  <th style={{ ...cell, textAlign: "right" }}>Value ({ordCcy})</th>
                  <th style={{ ...cell, textAlign: "right" }}>Value (₹)</th>
                </tr></thead>
                <tbody>
                  {(partDetail.orders || []).map((o, i) => (
                    <tr key={`o${i}`} style={o.spillover ? { background: "#FFFBEB" } : undefined}>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif" }}>{o.ms_pono || "—"}</td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif" }}>
                        {o.ms_custpo || "—"}
                        {o.spillover && <span style={{ marginLeft: 6, fontSize: 9.5, fontWeight: 800, color: "#fff", background: "#F59E0B", borderRadius: 4, padding: "1px 5px", fontFamily: "var(--omh-font, sans-serif)" }} title={`Overdue-unshipped, rolled forward from ${o.from_month}`}>SPILLOVER</span>}
                      </td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif", fontSize: 11 }}>{o.cust_po2 || <span className="muted">—</span>}</td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif", fontWeight: 700 }}>{fmtDate(o.target_date)}{o.spillover && o.from_month ? <span style={{ color: "#B45309", fontWeight: 700, fontSize: 10.5 }}> · from {o.from_month}</span> : null}</td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif", color: "#64748b" }}>{fmtDate(o.deliv_date)}</td>
                      <td style={rt}>{fmtNum(o.qty_ordered)}</td>
                      <td style={rt}>{fmtRate(o.price, o.currency)}</td>
                      <td style={rt} title={fmtCur(o.value_native, o.currency)}>{fmtCurCompact(o.value_native, o.currency)}</td>
                      <td style={rt} title={fmtCur(o.value_inr, "INR")}>{fmtCurCompact(o.value_inr, "INR")}</td>
                    </tr>
                  ))}
                  {(partDetail.orders || []).length === 0 && <tr><td colSpan={9} className="muted" style={{ padding: 16, textAlign: "center" }}>No open orders for this part this month.</td></tr>}
                </tbody>
                {(partDetail.orders || []).length > 0 && (
                  <tfoot><tr data-testid="drill-orders-totals">
                    <td style={ftl} colSpan={5}>Total · {partDetail.orders.length} orders</td>
                    <td style={ft}>{fmtNum(sum(partDetail.orders, "qty_ordered"))}</td>
                    <td style={ft}></td>
                    <td style={ft} title={fmtCur(sum(partDetail.orders, "value_native"), ordCcy)}>{fmtCurCompact(sum(partDetail.orders, "value_native"), ordCcy)}</td>
                    <td style={ft} title={fmtCur(sum(partDetail.orders, "value_inr"), "INR")}>{cur0(sum(partDetail.orders, "value_inr"))}</td>
                  </tr></tfoot>
                )}
              </table>

              <div style={{ padding: "16px 14px 4px", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, color: "#0369A1" }}>Invoices (shipped)</div>
              <table className="jde-grid" data-testid="drill-part-invoices" style={gridStyle}>
                <thead><tr>
                  <th style={cell}>Invoice #</th><th style={cell}>Sale date</th><th style={cell}>PO#</th>
                  <th style={{ ...cell, textAlign: "right" }}>Qty</th>
                  <th style={{ ...cell, textAlign: "right" }}>Unit Price</th>
                  <th style={{ ...cell, textAlign: "right" }}>Value ({invCcy})</th>
                  <th style={{ ...cell, textAlign: "right" }}>Value (₹)</th>
                </tr></thead>
                <tbody>
                  {(partDetail.invoices || []).map((v, i) => (
                    <tr key={`v${i}`}>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif", fontWeight: 700 }}>{v.ms_inv_no || "—"}</td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif" }}>{fmtDate(v.sale_date)}</td>
                      <td style={{ ...cell, fontFamily: "Inter, sans-serif" }}>{v.po_no || "—"}</td>
                      <td style={rt}>{fmtNum(v.qty)}</td>
                      <td style={rt}>{fmtRate(v.price, v.currency)}</td>
                      <td style={rt} title={fmtCur(v.value_native, v.currency)}>{fmtCurCompact(v.value_native, v.currency)}</td>
                      <td style={{ ...rt, fontWeight: 700 }} title={fmtCur(v.value_inr, "INR")}>{fmtCurCompact(v.value_inr, "INR")}</td>
                    </tr>
                  ))}
                  {(partDetail.invoices || []).length === 0 && <tr><td colSpan={7} className="muted" style={{ padding: 16, textAlign: "center" }}>No invoices for this part this month.</td></tr>}
                </tbody>
                {(partDetail.invoices || []).length > 0 && (
                  <tfoot><tr data-testid="drill-invoices-totals">
                    <td style={ftl} colSpan={3}>Total · {partDetail.invoices.length} invoices</td>
                    <td style={ft}>{fmtNum(sum(partDetail.invoices, "qty"))}</td>
                    <td style={ft}></td>
                    <td style={ft} title={fmtCur(sum(partDetail.invoices, "value_native"), invCcy)}>{fmtCurCompact(sum(partDetail.invoices, "value_native"), invCcy)}</td>
                    <td style={ft} title={fmtCur(sum(partDetail.invoices, "value_inr"), "INR")}>{cur0(sum(partDetail.invoices, "value_inr"))}</td>
                  </tr></tfoot>
                )}
              </table>
              </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
